import { gql } from '@apollo/client'
import { BlockFragment } from 'app/features/shared/blocks'
import { BaseOfferBlock } from '../blocks.types'
import {
  LinkOnlyRedemptionBlockFragment,
  MultipleCodesRedemptionBlockFragment,
  Redemption,
  SingleCodeRedemptionBlockFragment,
} from '../../redemptions'

export const OfferCollectionBlockFragment: BlockFragment = {
  name: 'offerCollection',
  fragment: gql`
    fragment offerCollection on OfferCollection {
      id
      blockName
      blockType
      offers {
        id
        name
        tag
        description
        thumbnailImage {
          id
          url
          alt
        }
        featuredImage {
          id
          url
          alt
        }
        brand {
          id
          slug
          logo {
            id
            url
            alt
          }
        }
        slug
        expiresAt
        offerLinkLabel
        offerLink
        offerLinkLabel
        redeemInList
        canRedeem
        redemptions {
          ${LinkOnlyRedemptionBlockFragment.partialFragment}
          ${MultipleCodesRedemptionBlockFragment.partialFragment}
          ${SingleCodeRedemptionBlockFragment.partialFragment}
        }
        isBookmarked
      }
      collection {
        id
        slug
      }
      offerCollectionDisplayStyle: displayStyle
      limit
      showEndTime
      backgroundColor
      collectionCardType
      linkToCollection
      title
      withMainFeaturedCard
      excludedPlatforms {
        ios
        android
        web
      }
    }
  `,
  partialFragment: `
    ...on OfferCollection {
      id
      blockName
      blockType
      offers {
        id
        name
        tag
        description
        featuredImage {
          id
          url
          alt
        }
        thumbnailImage {
          id
          url
          alt
        }
        slug
        expiresAt
        offerLinkLabel
        offerLink
        brand {
          id
          slug
          logo {
            id
            url
            alt
          }
        }
        offerLinkLabel
        redeemInList
        canRedeem
        redemptions {
          ${LinkOnlyRedemptionBlockFragment.partialFragment}
          ${MultipleCodesRedemptionBlockFragment.partialFragment}
          ${SingleCodeRedemptionBlockFragment.partialFragment}
        }
        isBookmarked
      }
       collection {
        id
        slug
      }
      offerCollectionDisplayStyle: displayStyle
      limit
      showEndTime
      backgroundColor
      collectionCardType
      linkToCollection
      title
      withMainFeaturedCard
      excludedPlatforms {
        ios
        android
        web
      }
    }
  `,
}

export const OfferCollectionBlockType = 'offerCollection'

export const canRenderOfferCollectionBlock = (block: OfferCollectionBlock): boolean => {
  // Do not render if the collection is missing
  if (!block.collection?.slug) {
    return false
  }

  // Do not render if there are no offers
  if (!block.offers?.length) {
    return false
  }

  return true
}

export interface OfferCollectionBlock extends BaseOfferBlock {
  blockType: typeof OfferCollectionBlockType
  offers: {
    id: string
    name: string
    thumbnailImage: {
      url: string
      alt: string
    }
    featuredImage: {
      url: string
      alt: string
    }
    slug: string
    description?: string
    tag?: string
    brand: {
      slug: string
      logo: {
        url: string
        alt: string
      }
    }
    expiresAt?: string
    offerLinkLabel?: string
    redeemInList: boolean
    canRedeem: boolean
    redemptions: Redemption[]
    isBookmarked: boolean
  }[]
  collection: {
    slug: string
  }
  offerCollectionDisplayStyle: string
  limit: number
  showEndTime: boolean
  backgroundColor?: string
  linkToCollection: boolean
  title?: string
  withMainFeaturedCard?: boolean
  collectionCardType?: 'complexView' | 'withBackgroundImage'
  imagePriority?: boolean
}

export type OfferCollectionDisplayStyle = 'carousel' | 'grid'
